import * as React from 'react'
import { Box, List, ListItem, ListItemButton, ListItemText, Menu } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Logout } from '@/icons'
import { useUiLanguageLabel } from '@/locales/uiLanguages'
import {
  navigationMenuPaperStyles,
  navigationListItemButtonStyles,
  listItemTextStyles,
  sectionHeaderTextStyles,
  checkmarkStyles,
  logoutButtonStyles,
  logoutTextStyles,
  listStyles,
  sectionListItemStyles,
  languageSectionListItemStyles,
  logoutListItemStyles,
  logoutIconStyles,
} from './MobileNavBarStyles'

type RouteMenu = {
  label: string
  icon?: string
}

type Route = {
  path: string
  menu?: RouteMenu
}

type Language = {
  tag: string
  display_name: string
}

type NavigationMenuProps = {
  anchorEl: HTMLElement | null
  routes: Route[]
  currentPath: string
  onClose: () => void
  onNavigate: (event: React.MouseEvent<HTMLElement>) => void
  onLogout: (event: React.MouseEvent) => void
  showLanguageInMenu?: boolean
  languages?: Language[]
  currentLanguage?: string
  onLanguageChange?: (languageCode: string, event: React.MouseEvent<HTMLElement>) => void
}

export const NavigationMenu = ({
  anchorEl,
  routes,
  currentPath,
  onClose,
  onNavigate,
  onLogout,
  showLanguageInMenu = false,
  languages = [],
  currentLanguage = '',
  onLanguageChange,
}: NavigationMenuProps) => {
  const { t } = useTranslation('general')
  const languageLabel = useUiLanguageLabel()

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      disableRestoreFocus
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          sx: navigationMenuPaperStyles,
        },
      }}>
      <List sx={listStyles}>
        {routes.length > 0 && (
          <>
            <ListItem sx={sectionListItemStyles}>
              <ListItemText primary={t('myAccount')} sx={sectionHeaderTextStyles} />
            </ListItem>
            {routes.map(({ path, menu }, index) => {
              if (!menu) return null
              const isActive = currentPath.includes(path)
              return (
                <ListItem key={`account-${index}`} disablePadding>
                  <ListItemButton
                    component="div"
                    selected={isActive}
                    onClick={(e) => {
                      e.currentTarget.setAttribute('data-path', path)
                      onNavigate(e as React.MouseEvent<HTMLElement>)
                    }}
                    sx={navigationListItemButtonStyles}>
                    <ListItemText primary={t(menu.label)} sx={listItemTextStyles(isActive)} />
                    {isActive && <Box sx={checkmarkStyles}>✓</Box>}
                  </ListItemButton>
                </ListItem>
              )
            })}
          </>
        )}

        {showLanguageInMenu && languages.length > 0 && onLanguageChange && (
          <>
            <ListItem sx={languageSectionListItemStyles(routes.length > 0)}>
              <ListItemText primary={t('language')} sx={sectionHeaderTextStyles} />
            </ListItem>
            {languages.map(({ tag, display_name }) => {
              const isSelected = tag === currentLanguage
              return (
                <ListItem key={tag} disablePadding>
                  <ListItemButton
                    component="div"
                    selected={isSelected}
                    onClick={(e) => onLanguageChange(tag, e)}
                    sx={navigationListItemButtonStyles}>
                    <ListItemText primary={languageLabel(tag, display_name)} sx={listItemTextStyles(isSelected)} />
                    {isSelected && <Box sx={checkmarkStyles}>✓</Box>}
                  </ListItemButton>
                </ListItem>
              )
            })}
          </>
        )}

        <ListItem disablePadding sx={logoutListItemStyles}>
          <ListItemButton onClick={onLogout} sx={logoutButtonStyles}>
            <Logout sx={logoutIconStyles} />
            <ListItemText primary={t('logout')} sx={logoutTextStyles} />
          </ListItemButton>
        </ListItem>
      </List>
    </Menu>
  )
}
